// The rail at the left of every place: Me (the mark) first, then the person's
// workspaces in the order they joined, then a way to make one. Choosing one
// moves this window there, to the page it was left on (`place-pages.ts`; the
// window's name follows). The place shown now has a bar at the rail's edge,
// and a workspace's tile turns squarer. A workspace with an icon shows it in
// the tile's place.
//
// The rail is data first (`useWorkspaceRail`, the outside's `ShellRail`): the
// rail here draws that data, and a native app's frame draws the same.
//
// Me's page opened from a workspace's own rows (me-place.ts) is that
// workspace's: its tile is the current one, and pressing Me goes back to Me's
// own sidebar.
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { m as cloud } from "@trameria/cloud-i18n";
import { m } from "@trameria/i18n";
import {
  PlaceRail,
  type ShellRail,
  type ShellRailPlace,
  useApi,
  useMeApp,
  wholeFileUrl,
  workspaceGroundIndex,
  workspaceInitial,
} from "@trameria/ui-core";
import { dropMePlace, useMePlace } from "./me-place";
import { ME, placeOfPath, usePlacePages } from "./place-pages";
import { workspacesQuery } from "./queries";

/**
 * The rail as data: each place goes to the page it was left on, or (when it
 * is the place shown now) to its home.
 */
export function useWorkspaceRail(): ShellRail {
  const list = useQuery(workspacesQuery(useApi())).data ?? [];
  const { apiBase } = useMeApp();
  const pages = usePlacePages();
  const path = useRouterState({
    select: (s) => placeOfPath(s.location.pathname),
  });
  const making = useRouterState({
    select: (s) => s.location.pathname === "/new",
  });
  const shown = useMePlace() ?? path;
  const place = (
    key: string,
    home: string,
    name: string,
    mark: ShellRailPlace["mark"],
  ): ShellRailPlace => {
    const current = shown === key;
    return {
      type: "place",
      id: `rail:${key}`,
      name,
      mark,
      to: current ? home : (pages[key] ?? home),
      ...(current ? { current: true } : {}),
    };
  };
  return {
    label: cloud.ws_rail(),
    entries: [
      place(ME, "/me", m.shell_rail_personal(), { kind: "brand" }),
      ...list.map((w) =>
        place(w.slug, `/${w.slug}`, w.name, {
          kind: "tile",
          initial: workspaceInitial(w.name),
          ground: workspaceGroundIndex(w.id),
          ...(w.iconFileId
            ? { image: wholeFileUrl(apiBase, w.iconFileId) }
            : {}),
        }),
      ),
      {
        type: "new",
        id: "rail:new",
        label: cloud.ws_rail_new(),
        to: "/new",
        ...(making ? { current: true } : {}),
      },
    ],
  };
}

const onSelect = (id: string) => {
  if (id === `rail:${ME}`) dropMePlace();
};

/** The rail (drawn by ui-core from `useWorkspaceRail`'s data). */
export function WorkspaceRail() {
  return <PlaceRail onSelect={onSelect} />;
}
